/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import {
    fetchOrganizationsThunk,
    saveOrganizationThunk,
    deleteOrganizationThunk
} from '../../actions/organization/OrganizationActions';
import { showSuccessThunk, showErrorThunk } from '@spike/notifications-action';
import { canManageOrganizations } from '../../auth/Auth';
import { Organization, SaveOrganization } from 'model/organization';
import { OrganizationStatus } from 'reducers/organizations/OrganizationState';

interface OrganizationsContextValue {
    // Data
    accounts: Organization[];
    parentAccounts: Organization[];
    childAccounts: Organization[];
    filteredParentAccounts: Organization[];
    filteredChildAccounts: Organization[];
    accountOptions: Array<{ label: string; value: string }>;
    loading: boolean;
    status: OrganizationStatus;
    organizationsCount: number;

    // Filter state
    selectedOption: string;
    searchText: string;
    selectedBusinessTypes: string[];

    // Modal state
    isAddAccountModalOpen: boolean;
    editingAccount: SaveOrganization | null;
    setEditingAccount: React.Dispatch<React.SetStateAction<SaveOrganization | null>>;

    // Permissions
    canAddAccounts: boolean;

    // Filter handlers
    handleStatusChange: (option: string) => void;
    handleSearchChange: (text: string) => void;
    setSelectedBusinessTypes: (types: string[]) => void;

    // Account handlers
    handleAddAccount: () => void;
    handleEditAccount: (account: Organization) => void;
    handleDeactivateAccount: (account: Organization) => void;
    handleActivateAccount: (account: Organization) => void;
    handleDeleteAccount: (account: Organization) => void;

    // Modal handlers
    handleCloseModal: () => void;
    handleSaveChildOrganizationAccount: (accountData: SaveOrganization, closeModalAfterSave?: boolean) => Promise<void>;

    //status toggle state and habdler
    handleToggleStatus: (action: string, account: Organization) => void;
    showConfirm: { show: boolean; action: string; account: Organization | null };
    setShowConfirm: React.Dispatch<
        React.SetStateAction<{ show: boolean; action: string; account: Organization | null }>
    >;
}

const OrganizationsContext = createContext<OrganizationsContextValue | undefined>(undefined);

interface OrganizationsProviderProps {
    children: ReactNode;
}

export const OrganizationsProvider: React.FC<OrganizationsProviderProps> = ({ children }) => {
    const dispatch = useDispatch();

    // Redux state
    const { businessTypes } = useSelector((state: RootState) => state.masterData.data);
    const { organizations, loading, status } = useSelector((state: RootState) => state.organizations);

    // Business types names for initial filter
    const businessTypesNames = useMemo(
        () => businessTypes.map((type: { id: string; name: string }) => type.name.toLowerCase()),
        [businessTypes]
    );

    // Local state - Filters
    const [selectedOption, setSelectedOption] = useState('active');
    const [searchText, setSearchText] = useState('');
    const [selectedBusinessTypes, setSelectedBusinessTypes] = useState<string[]>(businessTypesNames);

    // Local state - Modals
    const [isAddAccountModalOpen, setIsAddAccountModalOpen] = useState(false);

    const [editingAccount, setEditingAccount] = useState<SaveOrganization | null>(null);

    // Update selected business types when business types load
    useEffect(() => {
        if (businessTypesNames.length > 0 && selectedBusinessTypes.length === 0) {
            setSelectedBusinessTypes(businessTypesNames);
        }
    }, [businessTypesNames, selectedBusinessTypes.length]);

    // Fetch organizations on mount (only if not already loaded)
    useEffect(() => {
        const fetchAccounts = async () => {
            if (organizations.length === 0) {
                try {
                    await dispatch(fetchOrganizationsThunk() as any);
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error('Error fetching accounts:', error);
                    dispatch(showErrorThunk('Failed to load accounts. Please refresh the page.') as any);
                }
            }
        };

        fetchAccounts();
    }, [dispatch, organizations.length]);

    // Change status confirmation
    const [showConfirm, setShowConfirm] = React.useState({
        show: false,
        action: '',
        account: null as Organization | null
    });
    const handleToggleStatus = (action: string, account: Organization) => {
        setShowConfirm({ show: true, action, account });
    };
    const resetConfirm = () => {
        setShowConfirm({ show: false, action: '', account: null });
    };

    // Use organizations directly
    const accounts: Organization[] = useMemo(() => organizations, [organizations]);

    // Separate parent and child accounts
    const parentAccounts = useMemo(() => accounts.filter(account => account.parentOrganizationId === null), [accounts]);

    const childAccounts = useMemo(() => accounts.filter(account => account.parentOrganizationId !== null), [accounts]);

    // Account options for location modal
    const accountOptions = useMemo(
        () =>
            childAccounts.map(account => ({
                label: account.name,
                value: account.id.toString()
            })),
        [childAccounts]
    );

    // Helper function to check if an account or any of its descendants match filters
    const accountOrDescendantsMatch = useMemo(() => {
        const cache = new Map<number, boolean>();

        const checkMatch = (accountId: number): boolean => {
            if (cache.has(accountId)) {
                return cache.get(accountId)!;
            }

            const account = childAccounts.find(a => a.id === accountId);
            if (!account) {
                cache.set(accountId, false);
                return false;
            }

            // Check if account itself matches
            const matchesSearch =
                !searchText ||
                account.name.toLowerCase().includes(searchText.toLowerCase()) ||
                (account.businessType && account.businessType.toLowerCase().includes(searchText.toLowerCase())) ||
                (account.email && account.email.toLowerCase().includes(searchText.toLowerCase()));

            const matchesBusinessType =
                selectedBusinessTypes.length === 0 ||
                (account.businessType && selectedBusinessTypes.includes(account.businessType.toLowerCase()));

            const selfMatches = matchesSearch && matchesBusinessType;

            // Check if any descendants match
            const descendants = childAccounts.filter(child => child.parentOrganizationId === accountId);
            const descendantsMatch = descendants.some(child => checkMatch(child.id));

            const result = selfMatches || descendantsMatch;
            cache.set(accountId, result);
            return result;
        };

        return checkMatch;
    }, [childAccounts, searchText, selectedBusinessTypes]);

    // Filter child accounts (include if account matches OR if any descendant matches)
    const filteredChildAccounts = useMemo(
        () =>
            childAccounts.filter(account => {
                const matchesStatus =
                    selectedOption === 'all' ||
                    (selectedOption === 'active' && account.active) ||
                    (selectedOption === 'inactive' && !account.active);

                return matchesStatus && accountOrDescendantsMatch(account.id);
            }),
        [childAccounts, accountOrDescendantsMatch, selectedOption]
    );

    // Filter parent accounts (include if parent matches OR if any child matches)
    const filteredParentAccounts = useMemo(
        () =>
            parentAccounts.filter(account => {
                // Check if parent account matches status
                const parentMatchesStatus =
                    selectedOption === 'all' ||
                    (selectedOption === 'active' && account.active) ||
                    (selectedOption === 'inactive' && !account.active);

                // Check if parent account matches search
                const parentMatchesSearch =
                    !searchText ||
                    account.name.toLowerCase().includes(searchText.toLowerCase()) ||
                    (account.businessType && account.businessType.toLowerCase().includes(searchText.toLowerCase())) ||
                    (account.email && account.email.toLowerCase().includes(searchText.toLowerCase()));

                // Check if parent account matches business type
                const parentMatchesBusinessType =
                    selectedBusinessTypes.length === 0 ||
                    (account.businessType && selectedBusinessTypes.includes(account.businessType.toLowerCase()));

                // Check if any child account matches (already filtered by search, business type, and status)
                const hasMatchingChildren = filteredChildAccounts.some(
                    child => child.parentOrganizationId === account.id
                );

                // Include parent if it has matching children (regardless of parent's own filters)
                // OR if parent itself matches all filters
                if (hasMatchingChildren) {
                    return true;
                }

                return parentMatchesStatus && parentMatchesSearch && parentMatchesBusinessType;
            }),
        [parentAccounts, filteredChildAccounts, selectedOption, searchText, selectedBusinessTypes]
    );

    // Permissions
    const canAddAccounts = useMemo(() => canManageOrganizations(), []);

    // Filter handlers
    const handleStatusChange = useCallback((option: string) => {
        setSelectedOption(option);
    }, []);

    const handleSearchChange = useCallback((text: string) => {
        setSearchText(text);
    }, []);

    // Account handlers
    const handleAddAccount = useCallback(() => {
        setEditingAccount(null);
        setIsAddAccountModalOpen(true);
    }, []);

    const handleEditAccount = useCallback((account: Organization) => {
        setEditingAccount(account);
        setIsAddAccountModalOpen(true);
    }, []);

    const handleDeactivateAccount = useCallback(
        async (account: Organization) => {
            resetConfirm();
            try {
                const result = await dispatch(
                    saveOrganizationThunk({
                        ...account,
                        active: false
                    } as SaveOrganization) as any
                );

                // Only show success message if the thunk succeeded
                if (result) {
                    dispatch(showSuccessThunk('Account deactivated successfully') as any);
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error deactivating account:', error);
                dispatch(showErrorThunk('Failed to deactivate account. Please try again.') as any);
            }
        },
        [dispatch]
    );

    const handleActivateAccount = useCallback(
        async (account: Organization) => {
            resetConfirm();
            try {
                const result = await dispatch(
                    saveOrganizationThunk({
                        ...account,
                        active: true
                    } as SaveOrganization) as any
                );

                // Only show success message if the thunk succeeded
                if (result) {
                    dispatch(showSuccessThunk('Account activated successfully') as any);
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error activating account:', error);
                dispatch(showErrorThunk('Failed to activate account. Please try again.') as any);
            }
        },
        [dispatch]
    );

    const handleDeleteAccount = useCallback(
        async (account: Organization) => {
            resetConfirm();
            try {
                dispatch(deleteOrganizationThunk(account.id));
                dispatch(showSuccessThunk('Account deleted successfully') as any);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error deleting account:', error);
                dispatch(showErrorThunk('Failed to delete account. Please try again.') as any);
            }
        },
        [dispatch]
    );

    // Modal handlers
    const handleCloseModal = useCallback(() => {
        setIsAddAccountModalOpen(false);
        setEditingAccount(null);
    }, []);

    const handleSaveChildOrganizationAccount = useCallback(
        async (accountData: SaveOrganization, closeModalAfterSave = true) => {
            try {
                let result;
                if (editingAccount) {
                    const updatedData = {
                        ...editingAccount,
                        ...accountData
                    };
                    result = await dispatch(saveOrganizationThunk(updatedData) as any);
                } else {
                    result = await dispatch(saveOrganizationThunk(accountData) as any);
                }

                // Only show success and close modal if the thunk succeeded
                if (result) {
                    dispatch(
                        showSuccessThunk(
                            editingAccount ? 'Account updated successfully' : 'Account created successfully'
                        ) as any
                    );

                    // Only close modal and clear editing state if closeModalAfterSave is true
                    if (closeModalAfterSave) {
                        setIsAddAccountModalOpen(false);
                        setEditingAccount(null);
                    }
                }
                // Redux state will be updated automatically by the thunk
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error saving account:', error);
                dispatch(showErrorThunk('Failed to save account. Please try again.') as any);
                // Keep modal open on error so user can try again
            }
        },
        [editingAccount, dispatch]
    );

    const value: OrganizationsContextValue = {
        // Data
        accounts,
        parentAccounts,
        childAccounts,
        filteredParentAccounts,
        filteredChildAccounts,
        accountOptions,
        loading,
        status,
        organizationsCount: organizations.length,

        // Filter state
        selectedOption,
        searchText,
        selectedBusinessTypes,

        // Modal state
        isAddAccountModalOpen,
        editingAccount,
        setEditingAccount,

        // Permissions
        canAddAccounts,

        // Filter handlers
        handleStatusChange,
        handleSearchChange,
        setSelectedBusinessTypes,

        // Account handlers
        handleAddAccount,
        handleEditAccount,
        handleDeactivateAccount,
        handleActivateAccount,
        handleDeleteAccount,

        // Modal handlers
        handleCloseModal,
        handleSaveChildOrganizationAccount,

        //status toggle state and handler
        showConfirm,
        setShowConfirm,
        handleToggleStatus
    };

    return <OrganizationsContext.Provider value={value}>{children}</OrganizationsContext.Provider>;
};

export const useOrganizationsContext = (): OrganizationsContextValue => {
    const context = useContext(OrganizationsContext);
    if (!context) {
        throw new Error('useOrganizationsContext must be used within an OrganizationsProvider');
    }
    return context;
};

export default OrganizationsProvider;
